import { Component, ElementRef, Injector, effect, input, runInInjectionContext, signal, viewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { AiMarketingService } from '@admin-core/services/ai-marketing.service';
import {
  AiMarketingAssistantItem,
  AiMarketingMetric
} from '@admin-core/models/ai-marketing.model';
import { LanguageService } from '@admin-core/i18n/language.service';
import { TranslatePipe } from '@admin-core/i18n/translate.pipe';
import { ConfirmDialogService } from '@admin-shared/components/confirm-dialog/confirm-dialog.service';
import { AiMarketingFeatureHeaderComponent } from './ai-marketing-feature-header.component';

interface ChatTurn {
  role: 'user' | 'assistant';
  content: string;
  metrics?: AiMarketingMetric[];
  items?: AiMarketingAssistantItem[];
}

@Component({
  selector: 'app-ai-marketing-assistant',
  standalone: true,
  imports: [FormsModule, TranslatePipe, AiMarketingFeatureHeaderComponent],
  templateUrl: './ai-marketing-assistant.component.html',
  styleUrl: './ai-marketing-admin.scss'
})
export class AiMarketingAssistantComponent {
  readonly embedded = input(false);

  readonly scrollHost = viewChild<ElementRef<HTMLElement>>('scrollHost');

  readonly question = signal('');
  readonly busy = signal(false);
  readonly turns = signal<ChatTurn[]>([]);

  readonly suggestions = [
    'Which reel or video got the most views?',
    'What was the latest reel uploaded?',
    'Top reels on Instagram',
    'Best performing Facebook reel'
  ];

  constructor(
    private readonly api: AiMarketingService,
    private readonly i18n: LanguageService,
    private readonly confirmDialog: ConfirmDialogService,
    private readonly injector: Injector
  ) {
    runInInjectionContext(this.injector, () => {
      effect(() => {
        this.turns();
        this.busy();
        queueMicrotask(() => this.scrollToBottom());
      });
    });
  }

  useSuggestion(text: string): void {
    this.question.set(text);
  }

  send(): void {
    const q = this.question().trim();
    if (!q || this.busy()) {
      return;
    }

    this.busy.set(true);
    this.turns.update((list) => [...list, { role: 'user', content: q }]);
    this.question.set('');

    this.api
      .askAssistant({ question: q })
      .subscribe({
        next: (response) => {
          this.turns.update((list) => [
            ...list,
            {
              role: 'assistant',
              content: response.answer,
              metrics: response.items?.length
                ? undefined
                : response.metrics?.length
                  ? response.metrics
                  : undefined,
              items: response.items?.length ? response.items : undefined
            }
          ]);
          this.busy.set(false);
        },
        error: (err) => {
          this.busy.set(false);
          void this.confirmDialog.alert({
            title: this.i18n.t('common.errorTitle'),
            message: err?.error?.error ?? this.i18n.t('aiMarketing.loadError')
          });
        }
      });
  }

  displayAnswer(text: string, hasItems: boolean): string[] {
    const lines = this.answerLines(text);
    const isOrganicDump = lines.some(
      (line) => /^(top organic|link:|platform:|views:|caption:)/i.test(line) || line.startsWith('- ')
    );

    if (hasItems || isOrganicDump) {
      return lines.filter(
        (line) =>
          !/^(platform|views|caption|link|posted|top organic|also high)/i.test(line) &&
          !line.startsWith('- ')
      );
    }

    return lines;
  }

  showMetrics(turn: ChatTurn): boolean {
    if (turn.items?.length || !turn.metrics?.length) {
      return false;
    }

    return !this.answerLines(turn.content).some(
      (line) => /^(top organic|link:|platform:|views:|caption:)/i.test(line) || line.startsWith('- ')
    );
  }

  resolvePublicationUrl(url: string | null | undefined): string | null {
    const trimmed = (url ?? '').trim();
    if (!trimmed) {
      return null;
    }

    if (/^https?:\/\//i.test(trimmed)) {
      return trimmed;
    }

    if (trimmed.startsWith('/')) {
      return `https://www.facebook.com${trimmed}`;
    }

    return `https://www.facebook.com/${trimmed.replace(/^\//, '')}`;
  }

  answerLines(text: string): string[] {
    return text
      .split(/\n+/)
      .map((line) => line.trim())
      .filter(Boolean);
  }

  formatPostedAt(raw: string | null | undefined): string | null {
    if (!raw) return null;
    const date = new Date(raw);
    if (Number.isNaN(date.getTime())) return null;
    return date.toLocaleDateString(undefined, { dateStyle: 'medium' });
  }

  formatCount(value: number | null | undefined): string {
    if (value === null || value === undefined) {
      return this.i18n.t('common.unavailable');
    }
    return value.toLocaleString();
  }

  displayUrl(url: string): string {
    try {
      const parsed = new URL(url);
      const path =
        parsed.pathname.length > 36 ? `${parsed.pathname.slice(0, 33)}…` : parsed.pathname;
      return `${parsed.hostname}${path}`;
    } catch {
      return url.length > 48 ? `${url.slice(0, 45)}…` : url;
    }
  }

  private scrollToBottom(): void {
    const el = this.scrollHost()?.nativeElement;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  }
}
